import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './scroll-area.scss?inline'

/**
 * `dap-ds-scroll-area`
 * @summary A component that provides a customizable scrollable area with a custom scrollbar.
 * @element dap-ds-scroll-area
 * @title - Scroll area
 *
 * @slot - The content to be scrolled
 *
 * @csspart viewport - The viewport element that contains the scrollable content
 * @csspart scrollbar - The scrollbar container
 * @csspart corner - The corner between vertical and horizontal scrollbars
 *
 * @cssprop --dds-scroll-area-radius - The border radius of the scroll area and its elements (default: var(--dds-radius-base))
 * @cssprop --dds-scroll-area-background - The background color of the scroll area (default: var(--dds-background-neutral-base))
 * @cssprop --dds-scroll-area-scrollbar-background - The background color of the scrollbar (default: var(--dds-background-neutral-subtle))
 * @cssprop --dds-scroll-area-thumb-background - The background color of the scrollbar thumb (default: var(--dds-background-neutral-interactive))
 * @cssprop --dds-scroll-area-thumb-hover-background - The background color of the scrollbar thumb on hover (default: var(--dds-background-neutral-interactive-hover))
 * @cssprop --dds-scroll-area-thumb-active-background - The background color of the scrollbar thumb when active (default: var(--dds-background-neutral-interactive-active))
 * @cssprop --dds-scroll-area-transition - The transition timing for scrollbar interactions (default: var(--dds-transition-fast) var(--dds-easing-ease-out))
 * @cssprop --dds-scroll-area-scrollbar-size - The width/height of the scrollbar (default: 10px)
 */
export default class DapDSScrollArea extends DdsElement {
  static tagName = 'dap-ds-scroll-area'

  /**
   * The orientation of the scrollbar.
   * @type {'vertical' | 'horizontal' | 'both'}
   * @default 'vertical'
   */
  @property({ type: String }) orientation: 'vertical' | 'horizontal' | 'both' =
    'vertical'

  static readonly styles = unsafeCSS(styles)

  private viewport: HTMLElement | null = null
  private scrollbar: HTMLElement | null = null
  private thumbHorizontal: HTMLElement | null = null
  private thumbVertical: HTMLElement | null = null
  private isDragging = false
  private startY = 0
  private startScrollTop = 0

  firstUpdated() {
    this.viewport = this.shadowRoot?.querySelector(
      '.scroll-area__viewport',
    ) as HTMLElement
    this.scrollbar = this.shadowRoot?.querySelector(
      '.scroll-area__scrollbar',
    ) as HTMLElement
    this.thumbHorizontal = this.shadowRoot?.querySelector(
      '.scroll-area__thumb--horizontal',
    ) as HTMLElement
    this.thumbVertical = this.shadowRoot?.querySelector(
      '.scroll-area__thumb--vertical',
    ) as HTMLElement

    if (
      this.viewport &&
      this.scrollbar &&
      (this.thumbHorizontal || this.thumbVertical)
    ) {
      this.setupScrollbar()
    }
  }

  private setupScrollbar() {
    if (
      !this.viewport ||
      !this.scrollbar ||
      (!this.thumbHorizontal && !this.thumbVertical)
    )
      return

    const updateThumbSize = () => {
      if (this.orientation === 'vertical' || this.orientation === 'both') {
        const { scrollHeight, clientHeight } = this.viewport!
        const thumbHeight = (clientHeight / scrollHeight) * clientHeight
        this.thumbVertical!.style.height = `${thumbHeight}px`
      }

      if (this.orientation === 'horizontal' || this.orientation === 'both') {
        const { scrollWidth, clientWidth } = this.viewport!
        const thumbWidth = (clientWidth / scrollWidth) * clientWidth
        this.thumbHorizontal!.style.width = `${thumbWidth}px`
      }
    }

    const updateThumbPosition = () => {
      if (this.orientation === 'vertical' || this.orientation === 'both') {
        const { scrollTop, scrollHeight, clientHeight } = this.viewport!
        const thumbPosition =
          (scrollTop / (scrollHeight - clientHeight)) *
          (clientHeight - this.thumbVertical!.offsetHeight)
        this.thumbVertical!.style.transform = `translateY(${thumbPosition}px)`
      }

      if (this.orientation === 'horizontal' || this.orientation === 'both') {
        const { scrollLeft, scrollWidth, clientWidth } = this.viewport!
        const thumbPosition =
          (scrollLeft / (scrollWidth - clientWidth)) *
          (clientWidth - this.thumbHorizontal!.offsetWidth)
        this.thumbHorizontal!.style.transform = `translateX(${thumbPosition}px)`
      }
    }

    const handleThumbMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      this.isDragging = true

      if (this.orientation === 'vertical' || this.orientation === 'both') {
        this.startY = e.clientY
        this.startScrollTop = this.viewport!.scrollTop
      }

      if (this.orientation === 'horizontal' || this.orientation === 'both') {
        this.startY = e.clientX
        this.startScrollTop = this.viewport!.scrollLeft
      }

      document.addEventListener('mousemove', handleThumbMouseMove)
      document.addEventListener('mouseup', handleThumbMouseUp)
    }

    const handleThumbMouseMove = (e: MouseEvent) => {
      if (!this.isDragging) return

      if (this.orientation === 'vertical' || this.orientation === 'both') {
        const deltaY = e.clientY - this.startY
        const { scrollHeight, clientHeight } = this.viewport!
        const scrollRatio = scrollHeight / clientHeight
        this.viewport!.scrollTop = this.startScrollTop + deltaY * scrollRatio
      }

      if (this.orientation === 'horizontal' || this.orientation === 'both') {
        const deltaX = e.clientX - this.startY
        const { scrollWidth, clientWidth } = this.viewport!
        const scrollRatio = scrollWidth / clientWidth
        this.viewport!.scrollLeft = this.startScrollTop + deltaX * scrollRatio
      }
    }

    const handleThumbMouseUp = () => {
      this.isDragging = false
      document.removeEventListener('mousemove', handleThumbMouseMove)
      document.removeEventListener('mouseup', handleThumbMouseUp)
    }

    updateThumbSize()
    updateThumbPosition()

    this.viewport.addEventListener('scroll', updateThumbPosition)
    this.viewport.addEventListener('resize', updateThumbSize)
    this.thumbHorizontal?.addEventListener('mousedown', handleThumbMouseDown)
    this.thumbVertical?.addEventListener('mousedown', handleThumbMouseDown)
  }

  getScrollbar(orientation: 'horizontal' | 'vertical') {
    return html`<div
      part="scrollbar"
      class=${clsx(
        'scroll-area__scrollbar',
        `scroll-area__scrollbar--${orientation}`,
      )}>
      <div part="thumb" class="scroll-area__thumb--${orientation}"></div>
    </div> `
  }

  render() {
    return html`
      <div part="base" class="scroll-area">
        <div part="viewport" class="scroll-area__viewport">
          <slot></slot>
        </div>

        ${this.orientation === 'horizontal' || this.orientation === 'both'
          ? this.getScrollbar('horizontal')
          : nothing}
        ${this.orientation === 'vertical' || this.orientation === 'both'
          ? this.getScrollbar('vertical')
          : nothing}
      </div>
    `
  }
}
